/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import { PureComponent } from 'react';
import root from 'window-or-global';
import { RewardsModals } from '@/features/mergeRewards/components/RewardsModals';
import type { Props, State } from './types';
import { getDecodedCookiesSync, setCookieSync } from '@/utils/cookie';
import { analyticsCookie } from '@/constants/analytics/analyticsCookie';
import { useGetAllHeaderData } from '../api/useGetAllHeaderData';
import { trackPreferredVehicleData } from '@/features/ymme/utils/trackPreferredVehicleData';
import { isServer } from '@/utils/isServer';
import { usePreferredVehicle } from '../hooks/usePreferredVehicle';
import { useProductListView } from '@/features/shelf/context/useProductListView';
import { TopHeader } from './TopHeader';
import { useTaxonomyFilteredHeaderPromo } from '../hooks/useTaxonomyFilteredHeaderPromo';
import { localStorage } from '@/utils/localStorage';
import { OptionalSuspense } from '@/features/performance/components/OptionalSuspense';
import { useRewardDetails } from '@/features/rewards/api/getRewardDetails';
class HeaderComp extends PureComponent<Props, State> {
  eCookieId = false;

  componentDidMount() {
    const { preferredVehicle } = this.props;
    if (preferredVehicle?.catalogVehicleId) {
      trackPreferredVehicleData(preferredVehicle);
    }

    // Initialize eCookieId and utag data
    this.updateECookieId();
    this.updateUtagData();
    this.ensureCustomerIdCookies();
  }

  private updateECookieId(): void {
    if (!isServer) {
      const cookies = getDecodedCookiesSync(['eCookieId']);
      this.eCookieId = Boolean(cookies.get('eCookieId'));
    }
  }

  private updateUtagData(): void {
    if (this.eCookieId && typeof document !== 'undefined' && root.utag_data) {
      const utag = root.utag_data;
      const cookies = getDecodedCookiesSync(['eCookieId']);
      utag.eCookieId = cookies.get('eCookieId');
    }

    if (this.props.headerData) {
      const tokenId = this.props.headerData.apiResponseHeaders?.token_id;

      if (tokenId && typeof document !== 'undefined' && root.utag_data) {
        const utag = root.utag_data;
        const splitToken = tokenId.split('.');
        utag.jSessionId = splitToken[0] ? splitToken[0].toString() : '';
        utag.serverInfo = splitToken[1] ? splitToken[1].toString() : '';
      }
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { headerData, storeData, rewardsBalance } = this.props;
    const userType = headerData?.myAccountMap?.userType;
    const nddMarket = headerData?.analyticsData?.nddMarket;
    const nddHub = headerData?.analyticsData?.nddHubStore;
    const nddStore = headerData?.analyticsData?.nddStore;

    // Update eCookieId and utag data when headerData changes
    if (prevProps.headerData !== headerData) {
      this.updateECookieId();
      this.updateUtagData();
      this.ensureCustomerIdCookies();
    }

    /** Analytics start */

    if (typeof userType === 'string') {
      const currentUserTypeCookie = getDecodedCookiesSync([analyticsCookie.cookieUserType]);
      const currentUserType = currentUserTypeCookie.get(analyticsCookie.cookieUserType);
      if (currentUserType !== userType) {
        setCookieSync(analyticsCookie.cookieUserType, userType);
      }
    }

    if (typeof nddMarket === 'string') {
      const currentNddMarketCookie = getDecodedCookiesSync([analyticsCookie.cookieNddMarket]);
      const currentNddMarket = currentNddMarketCookie.get(analyticsCookie.cookieNddMarket);
      if (currentNddMarket !== nddMarket) {
        setCookieSync(analyticsCookie.cookieNddMarket, nddMarket);
      }
    }

    if (typeof nddHub === 'string') {
      const currentNddHubCookie = getDecodedCookiesSync([analyticsCookie.cookieNddHubStore]);
      const currentNddHub = currentNddHubCookie.get(analyticsCookie.cookieNddHubStore);
      if (currentNddHub !== nddHub) {
        setCookieSync(analyticsCookie.cookieNddHubStore, nddHub);
      }
    }

    if (typeof nddStore === 'string') {
      const currentNddStoreCookie = getDecodedCookiesSync([analyticsCookie.cookieNddStore]);
      const currentNddStore = currentNddStoreCookie.get(analyticsCookie.cookieNddStore);
      if (currentNddStore !== nddStore) {
        setCookieSync(analyticsCookie.cookieNddStore, nddStore);
      }
    }

    const newRewardBalance = String(rewardsBalance ?? '');
    const currentRewardBalanceCookie = getDecodedCookiesSync([
      analyticsCookie.cookieAvailableRewardBalance,
    ]);
    const currentRewardBalance = currentRewardBalanceCookie.get(
      analyticsCookie.cookieAvailableRewardBalance,
    );
    if (currentRewardBalance !== newRewardBalance) {
      setCookieSync(analyticsCookie.cookieAvailableRewardBalance, newRewardBalance);
    }

    if (prevProps.rewardsBalance !== rewardsBalance) {
      const n = Number(rewardsBalance);
      const normalizedValue = Number.isFinite(n) && n > 0 ? n : 0;
      setCookieSync(analyticsCookie.cookieRewardPoints, normalizedValue.toString());
    }

    if (prevProps.storeData !== storeData && storeData && storeData.storeNumber) {
      const { storeNumber, zip } = storeData;
      setCookieSync(analyticsCookie.cookiePreferredStoreId, storeNumber);

      const normalizedZip = typeof (zip as string | number) === 'string' ? zip : String(zip || '');
      if (normalizedZip.trim() !== '') {
        setCookieSync(analyticsCookie.cookiePreferredZipCode, normalizedZip);
        if (root.utag_data) {
          root.utag_data.preferredZipCode = normalizedZip;
        }
      }

      if (root.utag_data) {
        root.utag_data.preferredStoreId = storeNumber;
      }
    }
    /** Analytics end */

    const prevMyAccountMap = prevProps.headerData?.myAccountMap;
    const currentMyAccountMap = headerData?.myAccountMap;

    if (
      currentMyAccountMap &&
      typeof document !== 'undefined' &&
      (prevMyAccountMap?.profileId !== currentMyAccountMap.profileId ||
        prevMyAccountMap.rewardsId !== currentMyAccountMap.rewardsId)
    ) {
      const profileData = currentMyAccountMap.profileId || '';
      const rewardsData = currentMyAccountMap.rewardsId || '';

      let rewardsStatus = false;
      rewardsStatus = rewardsData !== '' && rewardsData.length > 0;

      if (root.utag_data) {
        root.utag_data.profileId = profileData;
        root.utag_data.rewardsId = rewardsData;
        root.utag_data.rewardsCustomerStatus = rewardsStatus;
      }
    }

    // for loggedin user vehicleInfo=undefined for 1st time
    const vehicleMapData = headerData?.vehicleMap; // for loggedin user vehicleInfo=undefined for 1st time

    if (
      vehicleMapData &&
      vehicleMapData.vehicleId !== prevProps.headerData?.vehicleMap?.vehicleId
    ) {
      trackPreferredVehicleData(vehicleMapData);
    } // change for Headerresponse reader
  }

  private ensureCustomerIdCookies(): void {
    const currentMyAccountMap = this.props.headerData?.myAccountMap;
    if (!currentMyAccountMap || typeof document === 'undefined') {
      return;
    }

    const { profileId = '', rewardsId = '' } = currentMyAccountMap;

    const cookies = getDecodedCookiesSync(['profileId', 'rewardsId']);
    const existingProfileId = cookies.get('profileId') ?? '';
    const existingRewardsId = cookies.get('rewardsId') ?? '';

    if (profileId && existingProfileId !== profileId) {
      setCookieSync('profileId', profileId, { path: '/' });
    }
    if (rewardsId && existingRewardsId !== rewardsId) {
      setCookieSync('rewardsId', rewardsId, { path: '/' });
    }
  }

  render() {
    const { headerData, headerPromo, rewardsBalance, preferredVehicle, isMobileFilterOpen } =
      this.props;

    const canShowToastNotification = localStorage.getItem('canShowToastNotification') === 'true';
    const rememberMe = headerData?.myAccountMap?.autoLogin === 'true';
    const showToast = canShowToastNotification && !rememberMe;

    return (
      <>
        {!isMobileFilterOpen && (
          <OptionalSuspense>
            <TopHeader
              showToast={showToast}
              preferredVehicle={preferredVehicle}
              headerPromo={headerPromo}
              rewardsBalance={rewardsBalance}
            />
          </OptionalSuspense>
        )}
        <RewardsModals />
      </>
    );
  }
}

export function Header() {
  const { isMobileFilterOpen } = useProductListView();
  const { headerData, headerPromo, storeData } = useGetAllHeaderData();
  const maybeCurrentTaxonomyFilteredPromo = useTaxonomyFilteredHeaderPromo();
  const preferredVehicle = usePreferredVehicle();
  const { data: rewardsData } = useRewardDetails();

  const rawRewardsBalance = Number(rewardsData?.rewardBalance ?? 0);
  const safeRewardsBalance = Number.isFinite(rawRewardsBalance) ? rawRewardsBalance : 0;
  const roundedRewardsBalance = Math.round(safeRewardsBalance * 100) / 100;

  return (
    <HeaderComp
      isMobileFilterOpen={isMobileFilterOpen}
      rewardsData={rewardsData}
      rewardsBalance={roundedRewardsBalance}
      headerData={headerData}
      headerPromo={maybeCurrentTaxonomyFilteredPromo ?? headerPromo}
      storeData={storeData}
      preferredVehicle={preferredVehicle}
    />
  );
}
